import { claude, requireKey, skill } from '../api.js';
import {
  ANALYSIS_KEYS,
  makeAnalysisChunkHandler,
  ANALYSIS_STEPS_DOCS,
  ANALYSIS_STEPS_QA,
  genConcept,
  startLoadingSteps,
  stopLoadingSteps,
} from '../build/concept.js';
import { getLogoVisionInput, hydrateBrief } from '../build/helpers.js';
import { $, mk, mkLogoInput, showErr } from '../dom.js';
import {
  buildDocAnalysisMessage,
  buildUploadConceptNote,
  canProceedUpload,
  getUsableImages,
  uploadNextScreen,
} from '../logic/uploads.ts';
import { analyseImages, docExt, mdToHtml, parseDoc } from '../parsers.js';
import { hasLogo, ST } from '../state.js';

// ── UPLOAD & DOCUMENT ANALYSIS ────────────────────────────────────
// File upload screen, document/image handling, doc analysis, Q&A enrichment.

// ── DOM RENDERING (becomes Vue <template> during migration) ──
// renderUploadScreen(), renderDocList(), renderImgGrid(), renderDocView()

/**
 * Render the upload screen — doc list, image grid, logo input, drag-drop wiring.
 */
function renderUploadScreen() {
  renderDocList();
  renderImgGrid();
  // Render logo input into dedicated section (re-render each visit to stay in sync with state)
  const logoSec = $('up-logo-section');
  if (logoSec) {
    // Preserve label, clear dynamic content after it
    while (logoSec.children.length > 1) {
      logoSec.removeChild(logoSec.lastChild);
    }
    logoSec.append(
      mkLogoInput(() => {
        updUploadProceedBtn();
      }),
    );
  }
  updUploadProceedBtn();
  // Auto-analyse any pending images when (re-)entering the upload screen
  const hasPending = ST.uploads.images.some(
    i => i.analysisStatus === 'pending' || i.analysisStatus === undefined,
  );
  if (hasPending && ST.apiKey) {
    scheduleImageAnalysis();
  }
  // Wire drag-drop
  const zone = $('doc-drop-zone');
  if (zone && !zone._wired) {
    zone._wired = true;
    zone.addEventListener('dragover', e => {
      e.preventDefault();
      zone.classList.add('drag-over');
    });
    zone.addEventListener('dragleave', () => {
      zone.classList.remove('drag-over');
    });
    zone.addEventListener('drop', e => {
      e.preventDefault();
      zone.classList.remove('drag-over');
      void handleDocFiles(e.dataTransfer.files);
    });
    $('doc-file-inp').addEventListener('change', e => {
      void handleDocFiles(e.target.files);
      e.target.value = '';
    });
    $('img-file-inp').addEventListener('change', e => {
      handleImgFiles(e.target.files);
      e.target.value = '';
    });
    // Image grid drag-drop
    const imgGrid = $('img-grid');
    if (imgGrid) {
      imgGrid.addEventListener('dragover', e => {
        e.preventDefault();
        imgGrid.classList.add('drag-over');
      });
      imgGrid.addEventListener('dragleave', e => {
        if (!imgGrid.contains(e.relatedTarget)) {
          imgGrid.classList.remove('drag-over');
        }
      });
      imgGrid.addEventListener('drop', e => {
        e.preventDefault();
        imgGrid.classList.remove('drag-over');
        const files = [...e.dataTransfer.files].filter(f => f.type.startsWith('image/'));
        if (files.length) {
          handleImgFiles(files);
        }
      });
    }
  }
}

// ── PURE LOGIC (extract to logic/uploads.js during Vue migration) ──
// handleDocFiles(), handleImgFiles(), scheduleImageAnalysis(),
// updUploadProceedBtn(), proceedFromUpload(), goToDocViewOrConcept()

/**
 * Process uploaded document files — parse each to markdown, update state.
 * @param {FileList} files
 */
async function handleDocFiles(files) {
  for (let fi = 0; fi < files.length; fi++) {
    const file = files[fi];
    const info = docExt(file.name);
    const entry = {
      name: file.name,
      ext: info.ext,
      icon: info.icon,
      markdown: '',
      status: 'processing',
      file,
    };
    ST.uploads.docs.push(entry);
    renderDocList();
    try {
      entry.markdown = await parseDoc(file);
      entry.status = 'done';
    } catch (e) {
      entry.status = 'error';
      entry.error = e.message;
    }
    renderDocList();
    updUploadProceedBtn();
  }
}

/**
 * Read uploaded image files as base64 and add to state.
 * Triggers image analysis scheduling after each file.
 * @param {FileList} files
 */
function handleImgFiles(files) {
  for (let fi = 0; fi < files.length; fi++) {
    const file = files[fi];
    (f => {
      const r = new FileReader();
      r.onload = ev => {
        ST.uploads.images.push({
          name: f.name,
          type: f.type,
          data: ev.target.result.split(',')[1],
          preview: ev.target.result,
          analysis: null,
          analysisStatus: 'pending',
        });
        renderImgGrid();
        updUploadProceedBtn();
        scheduleImageAnalysis();
      };
      r.readAsDataURL(f);
    })(file);
  }
}

/** Debounce image analysis — runs 800ms after the last image is added. */
function scheduleImageAnalysis() {
  if (ST.imgAnalysisTimer) {
    clearTimeout(ST.imgAnalysisTimer);
  }
  ST.imgAnalysisTimer = setTimeout(() => {
    ST.imgAnalysisTimer = null;
    if (!ST.apiKey) {
      return;
    }
    analyseImages();
  }, 800);
}

/** Render the document list with status indicators and view/remove actions. */
function renderDocList() {
  const c = $('doc-list');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  ST.uploads.docs.forEach((d, i) => {
    const row = mk('div', { class: 'doc-item' });
    const statusColor =
      d.status === 'done' ? 'var(--teal)' : d.status === 'error' ? 'var(--red)' : 'var(--acc)';
    const statusTxt =
      d.status === 'done' ? '✓ Ready' : d.status === 'error' ? '✗ Error' : 'Converting…';
    row.append(
      mk('span', { class: 'doc-icon' }, d.icon),
      mk('span', { class: 'doc-name' }, d.name),
      mk('span', { class: 'doc-status', style: `color:${statusColor}` }, statusTxt),
    );
    if (d.status === 'done') {
      const viewBtn = mk(
        'button',
        {
          class: 'btn btn-g btn-sm',
          onclick() {
            openDocView(i);
          },
        },
        'View',
      );
      row.append(viewBtn);
    }
    const rm = mk(
      'span',
      {
        class: 'doc-remove',
        onclick() {
          ST.uploads.docs.splice(i, 1);
          renderDocList();
          updUploadProceedBtn();
        },
      },
      '×',
    );
    row.append(rm);
    c.append(row);
  });
}

/** Render the image grid with analysis badges, retry, and add button. */
function renderImgGrid() {
  const c = $('img-grid');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  ST.uploads.images.forEach((img, i) => {
    const item = mk('div', { class: 'img-item' });
    const thumb = mk('img', { class: 'img-thumb', src: img.preview, alt: img.name });
    const rm = mk(
      'div',
      {
        class: 'img-remove',
        onclick(e) {
          e.stopPropagation();
          ST.uploads.images.splice(i, 1);
          renderImgGrid();
          updUploadProceedBtn();
        },
      },
      '×',
    );
    item.append(thumb, rm);
    if (img.analysisStatus === 'analyzing') {
      const overlay = mk('div', { class: 'img-analysis-loading' });
      overlay.append(mk('div', { class: 'sdot sd-spin' }));
      item.append(overlay);
    } else if (img.analysisStatus === 'error') {
      const badge = mk('div', { class: 'img-analysis-badge error' }, '✗ Analysis failed');
      const retry = mk(
        'div',
        {
          class: 'img-retry',
          onclick(e) {
            e.stopPropagation();
            img.analysisStatus = 'pending';
            renderImgGrid();
            if (ST.apiKey) {
              analyseImages();
            }
          },
        },
        '↻ Retry',
      );
      item.append(badge, retry);
    } else if (img.analysis) {
      const decision = img.analysis.useDecision;
      const badgeClass =
        decision === 'use' ? 'use' : decision === 'replace-with-stock' ? 'stock' : 'ai';
      const badgeText =
        decision === 'use'
          ? `✓ Use: ${img.analysis.placement}`
          : decision === 'replace-with-stock'
            ? '↻ Replace: stock'
            : '✦ Replace: AI';
      const badge = mk(
        'div',
        { class: `img-analysis-badge ${badgeClass}`, title: img.analysis.reason || '' },
        badgeText,
      );
      item.append(badge);
    }
    c.append(item);
  });
  // Add button
  const addBtn = mk(
    'div',
    {
      class: 'img-add',
      onclick() {
        $('img-file-inp').click();
      },
    },
    '＋',
    mk('span', { style: 'font-size:11px;margin-top:4px;color:var(--txt3)' }, 'Add image'),
  );
  c.append(addBtn);
}

/** Enable/disable the proceed button based on upload state and logo presence. */
function updUploadProceedBtn() {
  const btn = $('up-proceed-btn');
  if (!btn) {
    return;
  }
  btn.disabled = !canProceedUpload(ST.uploads.docs, ST.uploads.images, hasLogo());
}

/** Proceed from upload — analyse remaining images, then route to docview or concept. */
function proceedFromUpload() {
  if (!hasLogo()) {
    showErr('Please provide a logo before proceeding.');
    return;
  }
  const hasUnanalyzed = ST.uploads.images.some(
    i => i.analysisStatus !== 'done' && i.analysisStatus !== 'error',
  );
  if (ST.uploads.images.length > 0 && hasUnanalyzed) {
    requireKey(async () => {
      await analyseImages();
      goToDocViewOrConcept();
    });
  } else {
    goToDocViewOrConcept();
  }
}

function goToDocViewOrConcept() {
  const next = uploadNextScreen(ST.uploads.docs);
  if (next === 'docview') {
    G.go('docview');
  } else {
    requireKey(() => {
      genConceptFromUploads();
    });
  }
}

/**
 * Open a specific document in the docview screen.
 * @param {number} idx - Index in ST.uploads.docs
 */
function openDocView(idx) {
  // idx is the raw index in ST.uploads.docs; map to done-filtered index
  const doc = ST.uploads.docs[idx];
  const doneDocs = ST.uploads.docs.filter(d => d.status === 'done');
  const doneIdx = doneDocs.indexOf(doc);
  ST.activeDocIdx = doneIdx >= 0 ? doneIdx : 0;
  G.go('docview');
}

/** Render the document viewer — sidebar list, markdown content, and notes area. */
function renderDocView() {
  // Sidebar doc list
  const list = $('dv-doc-list');
  if (!list) {
    return;
  }
  list.innerHTML = '';
  ST.uploads.docs
    .filter(d => d.status === 'done')
    .forEach((d, i) => {
      const row = mk('div', {
        class: `dv-doc-row${i === ST.activeDocIdx ? ' active' : ''}`,
        onclick() {
          ST.activeDocIdx = i;
          renderDocView();
        },
      });
      row.append(
        mk('span', { style: 'font-size:16px' }, d.icon),
        mk('span', { class: 'dv-doc-row-name' }, d.name),
      );
      list.append(row);
    });
  // Content
  const content = $('dv-md-content');
  if (!content) {
    return;
  }
  const doneDocs = ST.uploads.docs.filter(d => d.status === 'done');
  const doc = doneDocs[ST.activeDocIdx];
  if (!doc) {
    content.innerHTML = '<p style="color:var(--txt3);padding:32px">No document selected.</p>';
    return;
  }
  content.innerHTML = mdToHtml(doc.markdown);
  // Notes
  const notes = $('dv-notes');
  if (notes) {
    notes.value = ST.docNotes;
  }
  if (notes) {
    notes.oninput = e => {
      ST.docNotes = e.target.value;
    };
  }
}

/** Proceed from docview — saves notes and runs document analysis. */
function proceedFromDocview() {
  if (!hasLogo()) {
    showErr('Please provide a logo before proceeding.');
    return;
  }
  ST.docNotes = $('dv-notes')?.value?.trim() || '';
  requireKey(() => {
    runDocAnalysis();
  });
}

// ── API CALLS (extract to composable during Vue migration) ──
// runDocAnalysis(), runQAEnrichment(), genConceptFromUploads()

/**
 * Run Claude AI document analysis — concatenates all parsed docs,
 * builds a marketing brief, and navigates to brief-review.
 */
async function runDocAnalysis() {
  G.go('loading');
  const logoPreview = ST.logoImg?.preview || ST.logoUrl || '';
  startLoadingSteps('Generating your Business DNA', ANALYSIS_STEPS_DOCS, { image: logoPreview });

  try {
    // Build structured message from docs + images + notes (logic/uploads.ts)
    const userMsg = buildDocAnalysisMessage(ST.uploads.docs, ST.uploads.images, ST.docNotes);

    // Include uploaded images for vision analysis
    const imgs = getUsableImages(ST.uploads.images);
    // Include logo so Claude can extract brand colors during brief generation
    const logoInput = getLogoVisionInput('Company logo \u2014 analyze colors, typography style, and visual identity for brand-aligned design tokens');
    if (logoInput) {
      imgs.unshift(logoInput);
    }

    const sk = skill('doc-analysis');
    const raw = await claude({
      sys: sk.prompt,
      user: userMsg,
      imgs: imgs.length ? imgs : [],
      tokens: sk.tokens || 5000,
      model: sk.model,
      temp: sk.temp,
      effort: sk.effort,
      schema: sk.schema,
      onChunk: makeAnalysisChunkHandler(ANALYSIS_KEYS),
    });

    const mb = JSON.parse(raw);

    // Images now come from site-structure per-section, not from the brief schema.
    // Just handle logo URL.
    if (ST.logoImg?.data) {
      const logoSrc =
        ST.logoImg.preview || `data:${ST.logoImg.type || 'image/png'};base64,${ST.logoImg.data}`;
      if (!mb.logoUrl) {
        mb.logoUrl = logoSrc;
      }
    } else if (ST.logoUrl?.trim() && !mb.logoUrl) {
      mb.logoUrl = ST.logoUrl;
    }

    mb.source = 'docs';
    mb.userEdits = {};
    ST.marketingBrief = hydrateBrief(mb);

    stopLoadingSteps();
    G.go('brief-review');
  } catch (e) {
    console.error('Doc analysis failed:', e);
    stopLoadingSteps();
    showErr('Brief generation failed: ' + e.message + '. Please try again.');
    G.go('upload');
  }
}

/**
 * Run Claude AI Q&A enrichment — builds a marketing brief from
 * brief answers and follow-up data, then navigates to brief-review.
 */
async function runQAEnrichment() {
  G.go('loading');
  startLoadingSteps('Generating your Business DNA', ANALYSIS_STEPS_QA);

  try {
    const b = ST.brief;
    const followUpAnswers = (ST.followUps || [])
      .filter(f => f.answer && !f.skipped)
      .map(f => `${f.question}: ${f.answer}`)
      .join('\n');
    const enrichment = ST.enrichment || {};

    const answerParts = [
      b.businessDesc ? `Business: ${b.businessDesc}` : '',
      b.customerProblem ? `Customer problem: ${b.customerProblem}` : '',
      b.desiredAction ? `Desired action: ${b.desiredAction}` : '',
      b.actionDetail ? `Action detail: ${b.actionDetail}` : '',
      b.leadMagnet ? `Lead magnet: ${b.leadMagnet}` : '',
      b.vibes?.length ? `Vibes: ${b.vibes.join(', ')}` : '',
      b.refsite ? `Reference site: ${b.refsite}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    const extraParts = [
      followUpAnswers ? `<follow_up_answers>\n${followUpAnswers}\n</follow_up_answers>` : '',
      enrichment.socialProof ? `<social_proof>${enrichment.socialProof}</social_proof>` : '',
      enrichment.objections?.length
        ? `<objections>${JSON.stringify(enrichment.objections)}</objections>`
        : '',
    ]
      .filter(Boolean)
      .join('\n\n');
    const userMsg = `<qa_answers>\n${answerParts}\n</qa_answers>${extraParts ? `\n\n${extraParts}` : ''}`;

    // Include logo so Claude can extract brand colors during brief generation
    const logoInput = getLogoVisionInput('Company logo \u2014 analyze colors, typography style, and visual identity for brand-aligned design tokens');

    const sk = skill('qa-enrichment');
    const raw = await claude({
      sys: sk.prompt,
      user: userMsg,
      imgs: logoInput ? [logoInput] : undefined,
      tokens: sk.tokens || 3000,
      model: sk.model,
      temp: sk.temp,
      effort: sk.effort,
      schema: sk.schema,
      onChunk: makeAnalysisChunkHandler(ANALYSIS_KEYS),
    });

    const mb = JSON.parse(raw);

    // Add logo to images if uploaded
    if (!mb.images) {
      mb.images = [];
    }
    if (ST.logoImg?.data) {
      const logoSrc =
        ST.logoImg.preview || `data:${ST.logoImg.type || 'image/png'};base64,${ST.logoImg.data}`;
      mb.images.unshift({
        src: logoSrc,
        alt: ST.logoImg.name || 'Logo',
        category: 'logo',
        selected: true,
      });
      if (!mb.logoUrl) {
        mb.logoUrl = logoSrc;
      }
    } else if (ST.logoUrl?.trim()) {
      mb.images.unshift({ src: ST.logoUrl, alt: 'Logo', category: 'logo', selected: true });
      if (!mb.logoUrl) {
        mb.logoUrl = ST.logoUrl;
      }
    }

    mb.source = 'questions';
    mb.vibes = b.vibes || [];
    mb.desiredAction = b.desiredAction || mb.desiredAction || '';
    mb.actionDetail = b.actionDetail || mb.actionDetail || '';
    mb.leadMagnet = b.leadMagnet || mb.leadMagnet || '';
    mb.userEdits = {};
    ST.marketingBrief = hydrateBrief(mb);

    stopLoadingSteps();
    G.go('brief-review');
  } catch (e) {
    console.error('QA enrichment failed:', e);
    stopLoadingSteps();
    showErr('Brief generation failed: ' + e.message + '. Please try again.');
    G.go('brief');
  }
}

/** Generate concept directly from uploaded documents and images. */
function genConceptFromUploads() {
  const note = buildUploadConceptNote(
    ST.uploads.docs,
    ST.uploads.images,
    ST.docNotes,
    $('up-notes')?.value?.trim() || '',
  );
  const imgs = getUsableImages(ST.uploads.images);
  genConcept(note, null, imgs.length ? imgs : []);
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  runDocAnalysis,
  runQAEnrichment,
  genConceptFromUploads,
  handleDocFiles,
  handleImgFiles,
  openDocView,
  proceedFromDocview,
  proceedFromUpload,
  renderDocList,
  renderDocView,
  renderImgGrid,
  renderUploadScreen,
  scheduleImageAnalysis,
  updUploadProceedBtn,
};

window.renderImgGrid = renderImgGrid;
